import clsx from 'clsx';
import * as React from 'react';
import {
    type NativeSyntheticEvent,
    TextInput,
    type TextInputKeyPressEventData,
} from 'react-native';
import type { SetterOrUpdater } from 'recoil';

import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import type { PopoverPlacement } from '../../Popover';
import { themedFonts, useCurrentTheme } from '../../theme';
import { typeQueryInPalette } from '../transforms';
import type { PaletteDef } from '../types';

/**
 * Input for the palette.
 */
export const PaletteQueryInput = React.forwardRef(function PaletteQueryInputWtihRef(
    props: {
        palette: PaletteDef;
        setPalette: SetterOrUpdater<PaletteDef | null>;
        withItems: boolean;
        placement: PopoverPlacement;
        onTab: () => void;
        withSearchIcon?: boolean;
    },
    ref: React.Ref<TextInput>
): React.ReactElement {
    const { palette, setPalette, withItems, onTab, withSearchIcon = false } = props;

    const [theme] = useCurrentTheme(themedFonts);

    const [query, setQuery] = React.useState(palette.search?.initialValue ?? palette.query.text);
    React.useEffect(() => {
        setQuery(palette.query.text);
    }, [palette.query.text]);

    const onChangeText = (text: string) => {
        setQuery(text);
        setPalette((p) => {
            if (!palette || !p) {
                // in some rare cases (due to concurrent mode),
                // we can be trying to type in a palette that's already closed
                return palette;
            }
            return typeQueryInPalette(p, text);
        });
    };

    const onKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (
            event.nativeEvent.key === 'Escape' ||
            (event.nativeEvent.key === 'Backspace' && palette.query.text.length === 0)
        ) {
            // Prevent default behavior of Backspace key which would delete selected text
            event.preventDefault();
            setPalette(null);
        } else if (event.nativeEvent.key === 'Tab') {
            // Prevent focus change on Tab press
            event.preventDefault();
            onTab();
        }
    };

    return (
        <div className={clsx(withItems && 'border-base border-b py-0.5')}>
            <div className="flex items-center">
                <div className="m-1 flex flex-1 items-center">
                    <div className="flex flex-1 items-center gap-3 px-2 py-1 text-muted text-ui-base">
                        {withSearchIcon && (
                            <Icon.Search
                                className="size-4"
                                style={{ color: theme.colors.text.light }}
                            />
                        )}
                        <TextInput
                            ref={ref}
                            value={query}
                            placeholder={palette.search?.placeholder}
                            placeholderTextColor={theme.colors.text.light}
                            onChangeText={onChangeText}
                            onKeyPress={onKeyPress}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <Button
                        kind="blank"
                        label="Close"
                        leadingIcon={Icon.X}
                        iconOnly
                        onPress={() => setPalette(null)}
                        className="block md:hidden"
                    />
                </div>
            </div>
        </div>
    );
});
